import type { ActivationRow } from './activation';
import { FUNCTIONS_URL, SUPABASE_ANON, supabase } from './supabase';

/** All calls hit SECURITY DEFINER RPCs gated on is_platform_admin() server-side. */

export interface Edition {
  plan_key: string;
  display_name: string;
  sort_order: number;
  monthly_price_cents: number | null;
  price_currency: string;
  stripe_price_id: string | null;
  is_contact_sales: boolean;
  /** Developer (tracker 47): internal, admin only, never sold. */
  is_internal?: boolean;
}

/** An edition's name as the admin lists show it: Developer carries its internal tag. */
export function editionDisplayName(edition: Pick<Edition, 'display_name' | 'is_internal'> | undefined, fallback = ''): string {
  if (!edition) return fallback;
  return edition.is_internal ? `${edition.display_name} (internal, not sold)` : edition.display_name;
}
export interface GrantRow { plan_key: string; capability_key: string; effect: 'enable' | 'disable'; }
export interface LevelRow { capability_key: string; maturity_level: number; }
export interface EditionMixRow { plan_key: string; display_name: string; workspace_count: number; is_internal?: boolean; }
export interface OverrideSummaryRow { capability_key: string; effect: string; override_count: number; }
export interface UsageRow { capability_key: string; event_count: number; last_used: string; }

export async function isPlatformAdmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc('is_platform_admin');
  return !error && data === true;
}

export async function listEditions(): Promise<Edition[]> {
  const { data } = await supabase.rpc('admin_list_editions');
  return (data as Edition[]) ?? [];
}

export async function setEditionPricing(
  planKey: string,
  monthlyPriceCents: number | null,
  currency: string,
  isContactSales: boolean
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_edition_pricing', {
    p_plan_key: planKey,
    p_monthly_price_cents: monthlyPriceCents,
    p_price_currency: currency,
    p_is_contact_sales: isContactSales,
  });
  if (error) throw new Error(error.message);
}

export async function getEditionMatrix(): Promise<GrantRow[]> {
  const { data } = await supabase.rpc('admin_get_edition_matrix');
  return (data as GrantRow[]) ?? [];
}

export async function setEditionGrant(planKey: string, capabilityKey: string, included: boolean): Promise<void> {
  const { error } = await supabase.rpc('admin_set_edition_grant', {
    p_plan_key: planKey,
    p_capability_key: capabilityKey,
    p_included: included,
  });
  if (error) throw new Error(error.message);
}

export async function getCapabilityLevels(): Promise<LevelRow[]> {
  const { data } = await supabase.rpc('admin_get_capability_levels');
  return (data as LevelRow[]) ?? [];
}

export async function setCapabilityLevel(capabilityKey: string, level: number): Promise<void> {
  const { error } = await supabase.rpc('admin_set_capability_level', {
    p_capability_key: capabilityKey,
    p_level: level,
  });
  if (error) throw new Error(error.message);
}

export async function editionMix(): Promise<EditionMixRow[]> {
  const { data } = await supabase.rpc('admin_edition_mix');
  return (data as EditionMixRow[]) ?? [];
}

export interface GlobalHold { capability_key: string; reason: string | null; updated_at: string; }

/** Capabilities held back for EVERY workspace (a global disable). No plan grant lifts one; deleting the row releases it. */
export async function globalHolds(): Promise<GlobalHold[]> {
  const { data, error } = await supabase.rpc('admin_global_capability_holds');
  if (error) throw new Error(error.message);
  return (data as GlobalHold[]) ?? [];
}

export async function overrideSummary(): Promise<OverrideSummaryRow[]> {
  const { data } = await supabase.rpc('admin_override_summary');
  return (data as OverrideSummaryRow[]) ?? [];
}

export async function activationFunnel(days = 90): Promise<ActivationRow[]> {
  const { data, error } = await supabase.rpc('admin_activation_funnel', { p_days: days });
  if (error) throw new Error(error.message);
  return (data as ActivationRow[]) ?? [];
}

export async function usageSummary(days = 30): Promise<UsageRow[]> {
  const { data } = await supabase.rpc('admin_usage_summary', { p_days: days });
  return (data as UsageRow[]) ?? [];
}

export interface PlatformAdminRow { user_id: string; email: string | null; note: string | null; created_at: string; }

export async function listPlatformAdmins(): Promise<PlatformAdminRow[]> {
  const { data } = await supabase.rpc('admin_list_platform_admins');
  return (data as PlatformAdminRow[]) ?? [];
}

/** Returns 'ok' | 'not_found' | 'exists'. */
export async function addPlatformAdmin(email: string): Promise<'ok' | 'not_found' | 'exists'> {
  const { data, error } = await supabase.rpc('admin_add_platform_admin', { p_email: email });
  if (error) throw new Error(error.message);
  return (data as 'ok' | 'not_found' | 'exists') ?? 'ok';
}

export async function removePlatformAdmin(userId: string): Promise<void> {
  const { error } = await supabase.rpc('admin_remove_platform_admin', { p_user_id: userId });
  if (error) throw new Error(error.message);
}

/** Creates the account (sign-up is closed) and emails a one-click sign-in link. */
export async function sendPlatformInvite(email: string, name?: string): Promise<{ existingAccount: boolean }> {
  const { data, error } = await supabase.functions.invoke('send-platform-invite', {
    body: { email, name: name?.trim() || undefined },
  });
  if (error) {
    // A non-2xx carries the function's own message in the response body.
    const body = await (error as { context?: Response }).context?.json?.().catch(() => null);
    throw new Error(body?.error ?? error.message);
  }
  return { existingAccount: data?.existingAccount === true };
}

// --- AI credit policy (monthly included credits per asset) ---------------------
// Three levels, resolved server-side by resolve_included_credits_per_asset():
// workspace allowance > edition override > global default.

export interface CreditPolicyRow {
  scope: 'global' | 'edition';
  scope_key: string;
  display_name: string;
  /** null on an edition = inherits the global default. */
  credits_per_asset: number | null;
  effective_credits_per_asset: number;
}

export interface WorkspaceAllowanceRow {
  account_id: string;
  account_name: string | null;
  edition_key: string | null;
  credits_per_asset_monthly: number;
  reason: string | null;
  effective_from: string;
  expires_at: string | null;
}

export interface WorkspaceRow {
  account_id: string;
  name: string | null;
  plan_key: string | null;
  member_count: number;
  edition_key: string | null;
  /** Tracker 47 dev-10: features AssetWRX switched on or off for this workspace only. */
  feature_settings?: number;
}

export interface WorkspaceCapabilityRow {
  source: 'grant' | 'override';
  scope_type: string;
  scope_id: string;
  capability_key: string;
  effect: 'enable' | 'disable';
  reason: string | null;
}

export interface CapabilityConfigurationMetaRow {
  account_id: string;
  workspace_name: string | null;
  edition_key: string | null;
  config_revision: number | string;
  revision_updated_at: string;
}

export interface WorkspaceCapabilityBillingRow {
  access_state: 'full' | 'read_only' | 'canceled';
  feature_entitlements: Record<string, unknown>;
}

export async function getCreditPolicy(): Promise<CreditPolicyRow[]> {
  const { data } = await supabase.rpc('admin_get_credit_policy');
  return (data as CreditPolicyRow[]) ?? [];
}

export async function setDefaultCreditsPerAsset(credits: number): Promise<void> {
  const { error } = await supabase.rpc('admin_set_default_credits_per_asset', { p_credits: credits });
  if (error) throw new Error(error.message);
}

/** `credits === null` clears the edition override so it inherits the global default. */
export async function setEditionCreditsPerAsset(planKey: string, credits: number | null): Promise<void> {
  const { error } = await supabase.rpc('admin_set_edition_credits_per_asset', {
    p_plan_key: planKey,
    p_credits: credits,
  });
  if (error) throw new Error(error.message);
}

export async function listWorkspaces(): Promise<WorkspaceRow[]> {
  const { data } = await supabase.rpc('admin_list_workspaces');
  return (data as WorkspaceRow[]) ?? [];
}

export async function setWorkspaceEdition(
  accountId: string,
  editionKey: string | null,
): Promise<{ edition_key: string | null; config_revision: number | string }> {
  const { data, error } = await supabase.rpc('admin_set_workspace_edition', {
    p_account_id: accountId,
    p_edition_key: editionKey,
  });
  if (error) throw new Error(error.message);
  const row = (data as Array<{ edition_key: string | null; config_revision: number | string }> | null)?.[0];
  if (!row) throw new Error('Edition update did not return control-plane state.');
  return row;
}

export async function getWorkspaceCapabilities(accountId: string): Promise<WorkspaceCapabilityRow[]> {
  const { data, error } = await supabase.rpc('admin_get_workspace_capabilities', {
    p_account_id: accountId,
  });
  if (error) throw new Error(error.message);
  return (data as WorkspaceCapabilityRow[]) ?? [];
}

export async function getCapabilityConfigurationMeta(
  accountId: string,
): Promise<CapabilityConfigurationMetaRow> {
  const { data, error } = await supabase.rpc('admin_get_capability_configuration_meta', {
    p_account_id: accountId,
  });
  if (error) throw new Error(error.message);
  const row = (data as CapabilityConfigurationMetaRow[] | null)?.[0];
  if (!row) throw new Error('Workspace capability metadata was not returned.');
  return row;
}

export async function getWorkspaceCapabilityBilling(
  accountId: string,
): Promise<WorkspaceCapabilityBillingRow> {
  const { data, error } = await supabase.rpc('admin_get_workspace_capability_billing', {
    p_account_id: accountId,
  });
  if (error) throw new Error(error.message);
  const row = (data as WorkspaceCapabilityBillingRow[] | null)?.[0];
  if (!row) throw new Error('Workspace billing capability state was not returned.');
  return row;
}

// --- Per-workspace feature settings (tracker 47 dev-10) -------------------------
// A platform admin switches one feature On or Off for one workspace, with a
// reason. It beats the edition (Matrix), loses to a global hold-back, and the
// workspace owner sees "Set by AssetWRX" and cannot change it.

export interface WorkspaceFeatureSettingRow {
  capability_key: string;
  effect: 'enable' | 'disable';
  reason: string | null;
  set_by_assetwrx: boolean;
  set_by_email: string | null;
  set_at: string;
}

export async function getWorkspaceFeatureSettings(accountId: string): Promise<WorkspaceFeatureSettingRow[]> {
  const { data, error } = await supabase.rpc('admin_workspace_feature_settings', { p_account_id: accountId });
  if (error) throw new Error(error.message);
  return (data as WorkspaceFeatureSettingRow[]) ?? [];
}

/** `effect === null` returns the feature to the plan default. */
export async function setWorkspaceCapability(
  accountId: string,
  capabilityKey: string,
  effect: 'enable' | 'disable' | null,
  reason: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_workspace_capability', {
    p_account_id: accountId,
    p_capability_key: capabilityKey,
    p_effect: effect,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

export async function listWorkspaceAllowances(): Promise<WorkspaceAllowanceRow[]> {
  const { data } = await supabase.rpc('admin_list_workspace_credit_allowances');
  return (data as WorkspaceAllowanceRow[]) ?? [];
}

export async function setWorkspaceAllowance(
  accountId: string,
  credits: number,
  reason: string | null,
  expiresAt: string | null
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_workspace_credit_allowance', {
    p_account_id: accountId,
    p_credits: credits,
    p_reason: reason,
    p_expires_at: expiresAt,
  });
  if (error) throw new Error(error.message);
}

export async function clearWorkspaceAllowance(accountId: string): Promise<void> {
  const { error } = await supabase.rpc('admin_clear_workspace_credit_allowance', {
    p_account_id: accountId,
  });
  if (error) throw new Error(error.message);
}

// --- Rate plan versions -------------------------------------------------------
// The pricing control plane: immutable versioned rate plans, the impact model,
// the publish workflow and the migration policy. Every call below is a
// SECURITY DEFINER RPC gated on is_platform_admin(); the rules the screen shows
// (a published version is immutable, a draft must be modelled in its current
// form before it can ship) are enforced there, not here.

export type RatePlanStatus = 'draft' | 'scheduled' | 'published' | 'archived';
export type MigrationStrategy = 'never' | 'immediate' | 'on_renewal' | 'after_notice';

export interface RatePlanSummary {
  id: string;
  version: number;
  currency: string;
  status: RatePlanStatus;
  note: string | null;
  created_at: string;
  published_at: string | null;
  workspace_count: number;
}

export interface RatePlanEdition {
  plan_key: string;
  display_name: string;
  sort_order: number;
  monthly_price_minor: number | null;
  annual_price_minor: number | null;
  /** Months of asset price a year costs on the annual term; null = not offered annually. */
  annual_asset_months: number | null;
  /** The most active assets the plan allows on this version; null = no limit. */
  max_assets: number | null;
  /** A free trial of the plan in days (card up front); null = no trial. */
  trial_days: number | null;
  /** One line on what the plan is for (app plan picker). */
  description: string | null;
  is_contact_sales: boolean;
  asset_rate_multiplier: number | string;
  included_credits_per_asset: number;
  is_featured: boolean;
  is_active: boolean;
}

export interface RatePlanBand {
  min_assets: number;
  max_assets: number | null;
  unit_minor: number;
  label: string | null;
}

export interface RatePlanPack {
  sku: string;
  display_name: string;
  units: number;
  price_minor: number;
  sort_order: number;
  is_active: boolean;
}

export interface RatePlanCreditCost {
  action_key: string;
  credits: number;
  display_label: string | null;
  public_key: string | null;
  public_sort_order: number | null;
}

export interface RatePlanTrial {
  trial_days: number;
  trial_asset_cap: number | null;
  free_first_assets: number;
}

/** Every money figure is in the currency named here. */
export interface RatePlanModelSummary {
  currency: string;
  workspaces_modelled: number;
  workspaces_unpriceable: number;
  workspaces_annual: number;
  current_mrr_minor: number;
  proposed_mrr_minor: number;
  delta_minor: number;
  delta_pct: number | null;
  increased: number;
  decreased: number;
  unchanged: number;
  newly_charged: number;
  over_threshold: number;
  largest_increase_minor: number | null;
  largest_increase_pct: number | null;
  /** How many of the modelled workspaces are on a live promo discount. */
  discounted_workspaces: number;
  /** What those discounts cost per month at today's prices. */
  discount_given_minor: number;
  /** Discounts ending within 90 days: a bill that jumps with no price change. */
  discounts_ending_90d: number;
}

export interface RatePlanModelRun {
  id: string;
  run_at: string;
  threshold_pct: number;
  summary: RatePlanModelSummary;
}

export interface RatePlanDetail {
  id: string;
  version: number;
  currency: string;
  status: RatePlanStatus;
  note: string | null;
  cloned_from: string | null;
  created_at: string;
  published_at: string | null;
  effective_from: string | null;
  /** Hash of every parameter. Publish refuses unless a model run matches it. */
  content_hash: string;
  workspace_count: number;
  /** null = not modelled in its current form, so publish would refuse. */
  model_run: RatePlanModelRun | null;
  editions: RatePlanEdition[];
  bands: RatePlanBand[];
  credit_packs: RatePlanPack[];
  credit_costs: RatePlanCreditCost[];
  trial: RatePlanTrial | null;
}

export interface RatePlanModelRow {
  account_id: string;
  workspace_name: string | null;
  edition_key: string | null;
  billing_term: string;
  billable_assets: number;
  current_version: number;
  /** What they actually pay today, after any live discount. */
  current_minor: number | null;
  /** What they would actually pay, after any live discount. */
  proposed_minor: number | null;
  delta_minor: number | null;
  delta_pct: number | null;
  current_credits: number | null;
  proposed_credits: number | null;
  priceable: boolean;
  /** What the price list says, before the discount. */
  current_list_minor: number | null;
  proposed_list_minor: number | null;
  discount_minor: number | null;
  promo_code: string | null;
}

export interface StripeReadinessRow {
  price_kind: 'assets' | 'edition' | 'edition_annual';
  plan_key: string | null;
  stripe_price_id: string | null;
  present: boolean;
  drifted: boolean;
  detail: string;
}

export interface WorkspacePinRow {
  account_id: string;
  name: string | null;
  edition_key: string | null;
  rate_plan_id: string;
  version: number;
  currency: string;
  status: RatePlanStatus;
  billing_term: 'monthly' | 'annual';
  pin_reason: string;
  pinned_at: string;
}

export interface RatePlanMigrationRow {
  id: string;
  from_version: number;
  to_version: number;
  currency: string;
  strategy: MigrationStrategy;
  notice_days: number | null;
  applies_to: Record<string, unknown>;
  override_reason: string | null;
  scheduled_at: string | null;
  executed_at: string | null;
  workspaces_moved: number | null;
  created_at: string;
}

export interface PricingAuditRow {
  created_at: string;
  table_name: string;
  event_type: 'insert' | 'update' | 'delete';
  record_key: string | null;
  actor_user_id: string | null;
  rate_plan_id: string | null;
  before_json: Record<string, unknown> | null;
  after_json: Record<string, unknown> | null;
}

function unwrap<T>(data: unknown, error: { message: string } | null): T {
  if (error) throw new Error(error.message);
  return data as T;
}

export async function listRatePlans(): Promise<RatePlanSummary[]> {
  const { data, error } = await supabase.rpc('admin_list_rate_plans');
  return unwrap<RatePlanSummary[]>(data ?? [], error);
}

export async function getRatePlan(id: string): Promise<RatePlanDetail> {
  const { data, error } = await supabase.rpc('admin_get_rate_plan', { p_rate_plan_id: id });
  return unwrap<RatePlanDetail>(data, error);
}

/** `currency` starts a new currency's version numbering; prices copy across 1:1. */
export async function cloneRatePlanToDraft(
  sourceId: string | null,
  note: string | null,
  currency: string | null,
): Promise<string> {
  const { data, error } = await supabase.rpc('admin_clone_rate_plan_to_draft', {
    p_source_rate_plan_id: sourceId,
    p_note: note,
    p_currency: currency,
  });
  return unwrap<string>(data, error);
}

export async function discardRatePlanDraft(id: string): Promise<void> {
  const { error } = await supabase.rpc('admin_discard_rate_plan_draft', { p_rate_plan_id: id });
  unwrap<null>(null, error);
}

/** An App Store / Google Play product and what it grants. The store sets its price. */
export interface StoreProductPlan {
  product_id: string;
  plan_key: string;
  /** Assets it holds; null = the plan's own limit. */
  max_assets: number | null;
  term: 'monthly' | 'annual';
  is_active: boolean;
}

export async function listStoreProductPlans(): Promise<StoreProductPlan[]> {
  const { data, error } = await supabase.rpc('admin_list_store_product_plans');
  return unwrap<StoreProductPlan[]>(data ?? [], error);
}

export async function setStoreProductPlan(row: StoreProductPlan): Promise<void> {
  const { error } = await supabase.rpc('admin_set_store_product_plan', {
    p_product_id: row.product_id,
    p_plan_key: row.plan_key,
    p_max_assets: row.max_assets,
    p_term: row.term,
    p_is_active: row.is_active,
  });
  unwrap<null>(null, error);
}

/** What a plan is called and its one-line description, on a draft version. */
export async function setRatePlanEditionCopy(
  id: string,
  planKey: string,
  displayName: string,
  description: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_rate_plan_edition_copy', {
    p_rate_plan_id: id,
    p_plan_key: planKey,
    p_display_name: displayName,
    p_description: description,
  });
  unwrap<null>(null, error);
}

export async function setRatePlanEdition(
  id: string,
  planKey: string,
  fields: {
    monthlyPriceMinor: number | null;
    annualPriceMinor?: number | null;
    isContactSales?: boolean | null;
    assetRateMultiplier?: number | null;
    includedCreditsPerAsset?: number | null;
    isFeatured?: boolean | null;
    /** Months of the monthly asset price charged for a year; 0 clears (no annual). */
    annualAssetMonths?: number | null;
    /** The most active assets the plan allows; 0 clears (no limit). */
    maxAssets?: number | null;
    /** A free trial of the plan in days (card up front); 0 clears (no trial). */
    trialDays?: number | null;
  },
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_rate_plan_edition', {
    p_rate_plan_id: id,
    p_plan_key: planKey,
    p_monthly_price_minor: fields.monthlyPriceMinor,
    p_annual_price_minor: fields.annualPriceMinor ?? null,
    p_is_contact_sales: fields.isContactSales ?? null,
    p_asset_rate_multiplier: fields.assetRateMultiplier ?? null,
    p_included_credits_per_asset: fields.includedCreditsPerAsset ?? null,
    p_is_featured: fields.isFeatured ?? null,
    p_annual_asset_months: fields.annualAssetMonths ?? null,
    p_max_assets: fields.maxAssets ?? null,
    p_trial_days: fields.trialDays ?? null,
  });
  unwrap<null>(null, error);
}

export async function setRatePlanBand(
  id: string,
  minAssets: number,
  maxAssets: number | null,
  unitMinor: number,
  label: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_rate_plan_band', {
    p_rate_plan_id: id,
    p_min_assets: minAssets,
    p_max_assets: maxAssets,
    p_unit_minor: unitMinor,
    p_label: label,
  });
  unwrap<null>(null, error);
}

export async function removeRatePlanBand(id: string, minAssets: number): Promise<void> {
  const { error } = await supabase.rpc('admin_remove_rate_plan_band', {
    p_rate_plan_id: id,
    p_min_assets: minAssets,
  });
  unwrap<null>(null, error);
}

export async function setRatePlanCreditPack(id: string, pack: RatePlanPack): Promise<void> {
  const { error } = await supabase.rpc('admin_set_rate_plan_credit_pack', {
    p_rate_plan_id: id,
    p_sku: pack.sku,
    p_display_name: pack.display_name,
    p_units: pack.units,
    p_price_minor: pack.price_minor,
    p_sort_order: pack.sort_order,
    p_is_active: pack.is_active,
  });
  unwrap<null>(null, error);
}

export async function removeRatePlanCreditPack(id: string, sku: string): Promise<void> {
  const { error } = await supabase.rpc('admin_remove_rate_plan_credit_pack', {
    p_rate_plan_id: id,
    p_sku: sku,
  });
  unwrap<null>(null, error);
}

// --- Add-ons (tracker 44) ----------------------------------------------------

export interface RatePlanAddon {
  key: string;
  name: string;
  description: string | null;
  unitMinor: number;
  annualMonths: number;
  trialDays: number;
  editions: string[];
  capabilityKeys: string[];
  countedOn: string;
  isActive: boolean;
  sortOrder: number;
  /** Workspaces with it on now (trial or paid), across versions. */
  workspaces?: number;
}

export async function listRatePlanAddons(id: string): Promise<RatePlanAddon[]> {
  const { data, error } = await supabase.rpc('admin_list_rate_plan_addons', {
    p_rate_plan_id: id,
  });
  return unwrap<RatePlanAddon[]>((data ?? []) as RatePlanAddon[], error);
}

export async function setRatePlanAddon(id: string, a: RatePlanAddon): Promise<void> {
  const { error } = await supabase.rpc('admin_set_rate_plan_addon', {
    p_rate_plan_id: id,
    p_addon_key: a.key,
    p_display_name: a.name,
    p_description: a.description,
    p_unit_minor: a.unitMinor,
    p_annual_months: a.annualMonths,
    p_trial_days: a.trialDays,
    p_editions: a.editions,
    p_capability_keys: a.capabilityKeys,
    p_counted_on: a.countedOn,
    p_is_active: a.isActive,
    p_sort_order: a.sortOrder,
  });
  unwrap<null>(null, error);
}

export async function removeRatePlanAddon(id: string, key: string): Promise<void> {
  const { error } = await supabase.rpc('admin_remove_rate_plan_addon', {
    p_rate_plan_id: id,
    p_addon_key: key,
  });
  unwrap<null>(null, error);
}

export type AddonPlanState = 'off' | 'addon' | 'included';

/** For the Matrix: each switch an add-on turns on -> that add-on's key. */
export async function getAddonSwitches(): Promise<Record<string, string>> {
  const { data, error } = await supabase.rpc('admin_addon_switches');
  return unwrap<Record<string, string>>((data ?? {}) as Record<string, string>, error);
}

/** Workspaces on this plan that have the add-on on now (trial or paid). */
export async function getAddonPlanImpact(addonKey: string, planKey: string): Promise<number> {
  const { data, error } = await supabase.rpc('admin_addon_plan_impact', {
    p_addon_key: addonKey,
    p_plan_key: planKey,
  });
  return unwrap<number>(Number(data ?? 0), error);
}

/** Off / Add-on / Included for one plan; returns how many workspaces' add-ons ended. */
export async function setAddonPlanState(addonKey: string, planKey: string, state: AddonPlanState): Promise<number> {
  const { data, error } = await supabase.rpc('admin_set_addon_plan_state', {
    p_addon_key: addonKey,
    p_plan_key: planKey,
    p_state: state,
  });
  return unwrap<number>(Number(data ?? 0), error);
}

/** For the Matrix: each switch an add-on turns on -> the plans that can buy it. */
export async function getAddonMatrix(): Promise<Record<string, string[]>> {
  const { data, error } = await supabase.rpc('admin_addon_matrix');
  return unwrap<Record<string, string[]>>((data ?? {}) as Record<string, string[]>, error);
}

export async function setRatePlanCreditCost(
  id: string,
  actionKey: string,
  credits: number,
  displayLabel: string | null,
  publicKey: string | null,
  publicSortOrder: number | null,
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_rate_plan_credit_cost', {
    p_rate_plan_id: id,
    p_action_key: actionKey,
    p_credits: credits,
    p_display_label: displayLabel,
    p_public_key: publicKey,
    p_public_sort_order: publicSortOrder,
  });
  unwrap<null>(null, error);
}

/** Takes an action off the public pricing page without changing its price. */
export async function unpublishRatePlanCreditCost(id: string, actionKey: string): Promise<void> {
  const { error } = await supabase.rpc('admin_unpublish_rate_plan_credit_cost', {
    p_rate_plan_id: id,
    p_action_key: actionKey,
  });
  unwrap<null>(null, error);
}

export async function setRatePlanTrial(
  id: string,
  trialDays: number,
  trialAssetCap: number | null,
  freeFirstAssets: number,
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_rate_plan_trial', {
    p_rate_plan_id: id,
    p_trial_days: trialDays,
    p_trial_asset_cap: trialAssetCap,
    p_free_first_assets: freeFirstAssets,
  });
  unwrap<null>(null, error);
}

/** Records the run against the draft's current content hash. */
export async function runRatePlanModel(id: string, thresholdPct = 25): Promise<RatePlanModelSummary> {
  const { data, error } = await supabase.rpc('admin_run_rate_plan_model', {
    p_rate_plan_id: id,
    p_threshold_pct: thresholdPct,
  });
  return unwrap<RatePlanModelSummary>(data, error);
}

export async function modelRatePlan(id: string): Promise<RatePlanModelRow[]> {
  const { data, error } = await supabase.rpc('admin_model_rate_plan', { p_rate_plan_id: id });
  return unwrap<RatePlanModelRow[]>(data ?? [], error);
}

export interface StripeSyncReport {
  mode: 'test' | 'live';
  applied: boolean;
  prices: Array<{ price_kind: string; plan_key: string | null; reason?: string; would_create?: Record<string, unknown>; created?: string; skipped?: string }>;
  coupons: Array<{ kind: string; code: string; reason?: string }>;
  errors: string[];
}

/**
 * Pushes a version's Prices (and the promo Coupons) into Stripe. Without
 * `apply` it only reports what it WOULD create; nothing is sent to Stripe.
 */
export async function syncRatePlanToStripe(id: string, apply: boolean): Promise<StripeSyncReport> {
  const { data, error } = await supabase.functions.invoke('pricing-stripe-sync', {
    body: { ratePlanId: id, apply },
  });
  if (error) {
    const body = await (error as { context?: Response }).context?.json?.().catch(() => null);
    throw new Error(body?.errors?.join('; ') || body?.error || error.message);
  }
  return data as StripeSyncReport;
}

export async function rateplanStripeReadiness(id: string, mode = 'test'): Promise<StripeReadinessRow[]> {
  const { data, error } = await supabase.rpc('admin_rate_plan_stripe_readiness', {
    p_rate_plan_id: id,
    p_mode: mode,
  });
  return unwrap<StripeReadinessRow[]>(data ?? [], error);
}

export interface PublishOptions {
  strategy: MigrationStrategy;
  noticeDays: number | null;
  appliesTo: Record<string, unknown>;
  overrideReason: string | null;
  thresholdPct: number;
  requireStripe: boolean;
  stripeMode: string;
}

export async function publishRatePlan(id: string, options: PublishOptions): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.rpc('admin_publish_rate_plan', {
    p_rate_plan_id: id,
    p_strategy: options.strategy,
    p_notice_days: options.noticeDays,
    p_applies_to: options.appliesTo,
    p_override_reason: options.overrideReason,
    p_threshold_pct: options.thresholdPct,
    p_require_stripe: options.requireStripe,
    p_stripe_mode: options.stripeMode,
  });
  return unwrap<Record<string, unknown>>(data, error);
}

export async function executeRatePlanMigration(migrationId: string): Promise<number> {
  const { data, error } = await supabase.rpc('admin_execute_rate_plan_migration', {
    p_migration_id: migrationId,
  });
  return unwrap<number>(data ?? 0, error);
}

export async function listRatePlanMigrations(id: string | null = null): Promise<RatePlanMigrationRow[]> {
  const { data, error } = await supabase.rpc('admin_list_rate_plan_migrations', { p_rate_plan_id: id });
  return unwrap<RatePlanMigrationRow[]>(data ?? [], error);
}

export async function listWorkspaceRatePlans(): Promise<WorkspacePinRow[]> {
  const { data, error } = await supabase.rpc('admin_list_workspace_rate_plans');
  return unwrap<WorkspacePinRow[]>(data ?? [], error);
}

export async function setWorkspaceBillingTerm(accountId: string, term: 'monthly' | 'annual'): Promise<void> {
  const { error } = await supabase.rpc('admin_set_workspace_billing_term', {
    p_account_id: accountId,
    p_term: term,
  });
  unwrap<null>(null, error);
}

/** Repins the workspace to the published version of that currency. */
export async function setWorkspaceBillingCurrency(accountId: string, currency: string): Promise<void> {
  const { error } = await supabase.rpc('admin_set_workspace_billing_currency', {
    p_account_id: accountId,
    p_currency: currency,
  });
  unwrap<null>(null, error);
}

export interface CollaboratorRow {
  account_id: string;
  account_name: string;
  email: string | null;
  joined_as: string | null;
  invited_by_account_id: string | null;
  invited_by_name: string | null;
  workspace_mode: string;
  edition_key: string | null;
  joined_at: string;
  last_active_at: string | null;
  jobs_done: number;
  requests_raised: number;
  reports_reviewed: number;
}

/** Everyone who signed up through an invite to work, newest first (the prospect list). */
export async function listCollaborators(): Promise<CollaboratorRow[]> {
  const { data, error } = await supabase.rpc('admin_list_collaborators', { p_limit: 1000 });
  return unwrap<CollaboratorRow[]>(data ?? [], error);
}

/** How many of a workspace's AI credits each outside person (contractor, requester) may spend a month on its work; a workspace can change its own. */
export async function collaboratorAiAllowance(): Promise<number> {
  const { data, error } = await supabase.rpc('admin_collaborator_ai_allowance');
  return Number(unwrap<number>(data, error) ?? 0);
}

export async function setCollaboratorAiAllowance(credits: number): Promise<void> {
  const { error } = await supabase.rpc('admin_set_collaborator_ai_allowance', { p_credits: credits });
  unwrap<null>(null, error);
}

/** The currency the marketing site presents and new workspaces bill in. */
export async function setPricingCatalogCurrency(currency: string): Promise<void> {
  const { error } = await supabase.rpc('admin_set_pricing_catalog_currency', { p_currency: currency });
  unwrap<null>(null, error);
}

/**
 * Which currency the public site is actually showing right now.
 *
 * Read from the same anon-facing snapshot the marketing build consumes, not
 * from a setting the portal keeps its own copy of: the point of the line on
 * the price sheet is to report what the outside world sees. This is the one
 * call the screen makes that is deliberately not admin-gated - it is the price
 * list - so the database test keeps it in a separate list from the rest.
 */
export async function siteCatalogCurrency(): Promise<string | null> {
  const { data, error } = await supabase.rpc('public_pricing_snapshot');
  if (error) return null;
  const snapshot = (data ?? null) as { catalogCurrency?: string } | null;
  return snapshot?.catalogCurrency ?? null;
}

export interface AssetChargeLine {
  from_asset: number;
  to_asset: number;
  units: number;
  unit_minor: number;
  subtotal_minor: number;
}

/**
 * The worked example beside the bands. It comes from the server because
 * compute_asset_charge_breakdown is the function that bills: a second
 * implementation in the browser is exactly the drift this control plane exists
 * to remove.
 */
export async function previewAssetCharge(
  id: string,
  planKey: string,
  assetCount: number,
): Promise<AssetChargeLine[]> {
  const { data, error } = await supabase.rpc('admin_preview_asset_charge', {
    p_rate_plan_id: id,
    p_plan_key: planKey,
    p_asset_count: assetCount,
  });
  return unwrap<AssetChargeLine[]>(data ?? [], error);
}

export async function pricingAudit(ratePlanId: string | null = null, limit = 60): Promise<PricingAuditRow[]> {
  const { data, error } = await supabase.rpc('admin_get_pricing_audit', {
    p_rate_plan_id: ratePlanId,
    p_limit: limit,
  });
  return unwrap<PricingAuditRow[]>(data ?? [], error);
}

// --- Promo codes ---------------------------------------------------------------
// Inside the rate-plan section on purpose: check-rpc-contract scopes from the
// marker above to the end of the file, so these calls are held to the same
// name-and-argument contract as the rest of the pricing screen.

export interface PromoCodeRow {
  id: string;
  code: string;
  description: string | null;
  percent_off: number | string;
  duration_months: number | null;
  applies_to: 'everything' | 'plan_fee' | 'assets';
  max_redemptions: number | null;
  valid_from: string;
  valid_until: string | null;
  status: 'active' | 'archived';
  note: string | null;
  created_at: string;
  redemption_count: number;
  live_count: number;
}

export interface PromoRedemptionRow {
  id: string;
  promo_code_id: string;
  code: string;
  account_id: string;
  workspace_name: string | null;
  percent_off: number | string;
  duration_months: number | null;
  applies_to: string;
  starts_at: string;
  ends_at: string | null;
  redeemed_at: string;
  source: string;
  revoked_at: string | null;
  revoked_reason: string | null;
  is_live: boolean;
}

export async function listPromoCodes(): Promise<PromoCodeRow[]> {
  const { data, error } = await supabase.rpc('admin_list_promo_codes');
  return unwrap<PromoCodeRow[]>(data ?? [], error);
}

export async function listPromoRedemptions(promoCodeId: string | null = null): Promise<PromoRedemptionRow[]> {
  const { data, error } = await supabase.rpc('admin_list_promo_redemptions', {
    p_promo_code_id: promoCodeId,
  });
  return unwrap<PromoRedemptionRow[]>(data ?? [], error);
}

export interface NewPromoCode {
  code: string;
  percentOff: number;
  durationMonths: number | null;
  appliesTo: 'everything' | 'plan_fee' | 'assets';
  maxRedemptions: number | null;
  validUntil: string | null;
  description: string | null;
  note: string | null;
}

export async function createPromoCode(fields: NewPromoCode): Promise<string> {
  const { data, error } = await supabase.rpc('admin_create_promo_code', {
    p_code: fields.code,
    p_percent_off: fields.percentOff,
    p_duration_months: fields.durationMonths,
    p_applies_to: fields.appliesTo,
    p_max_redemptions: fields.maxRedemptions,
    p_valid_until: fields.validUntil,
    p_description: fields.description,
    p_note: fields.note,
  });
  return unwrap<string>(data, error);
}

/**
 * Only the fields that cannot reach somebody who already redeemed. The
 * commercial terms are not parameters at all, rather than being parameters the
 * server then refuses.
 */
export async function updatePromoCode(
  id: string,
  fields: { description?: string | null; maxRedemptions: number | null; validUntil: string | null; note?: string | null },
): Promise<void> {
  const { error } = await supabase.rpc('admin_update_promo_code', {
    p_promo_code_id: id,
    p_description: fields.description ?? null,
    p_max_redemptions: fields.maxRedemptions,
    p_valid_until: fields.validUntil,
    p_note: fields.note ?? null,
  });
  unwrap<null>(null, error);
}

export async function archivePromoCode(id: string): Promise<void> {
  const { error } = await supabase.rpc('admin_archive_promo_code', { p_promo_code_id: id });
  unwrap<null>(null, error);
}

export async function applyPromoCode(
  promoCodeId: string,
  accountId: string,
  note: string | null = null,
): Promise<string> {
  const { data, error } = await supabase.rpc('admin_apply_promo_code', {
    p_promo_code_id: promoCodeId,
    p_account_id: accountId,
    p_note: note,
  });
  return unwrap<string>(data, error);
}

export async function revokePromoRedemption(redemptionId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('admin_revoke_promo_redemption', {
    p_redemption_id: redemptionId,
    p_reason: reason,
  });
  unwrap<null>(null, error);
}

export interface DiscountExposureRow {
  currency: string;
  discounted_workspaces: number;
  discount_given_minor: number;
  ending_within_90d: number;
  next_ending_at: string | null;
}

/**
 * What promo discounts are costing, per currency. Its own call rather than
 * making the price sheet run the whole impact model, which prices every
 * workspace twice.
 */
export async function discountExposure(currency: string | null = null): Promise<DiscountExposureRow[]> {
  const { data, error } = await supabase.rpc('admin_discount_exposure', { p_currency: currency });
  return unwrap<DiscountExposureRow[]>(data ?? [], error);
}

export interface PromoStripeReadinessRow {
  promo_code_id: string;
  code: string;
  status: string;
  stripe_coupon_id: string | null;
  stripe_promotion_code_id: string | null;
  present: boolean;
  drifted: boolean;
  active_mismatch: boolean;
  blocked_on_prices: boolean;
  redemptions_unsynced: number;
  detail: string;
}

/** Whether Stripe carries each offer, and every way the two can disagree. */
export async function promoStripeReadiness(mode = 'test'): Promise<PromoStripeReadinessRow[]> {
  const { data, error } = await supabase.rpc('admin_promo_stripe_readiness', { p_mode: mode });
  return unwrap<PromoStripeReadinessRow[]>(data ?? [], error);
}

// ── Vision training (docs/vision/SPECIALIST_MODELS_ARCHITECTURE.md) ──────────
/** One label's held-out numbers (benchmark.ts `LabelSummary`). */
export interface VisionLabelSummary {
  positives: number;
  negatives: number;
  tp: number;
  fn: number;
  fp: number;
  tn: number;
  precision: number | null;
  recall: number | null;
  f1: number | null;
  recallInterval: { low: number; high: number } | null;
  enoughExamples: boolean;
  meetsPrecision: boolean | null;
  meetsRecall: boolean | null;
  pass: boolean;
  note: string;
}
export interface VisionPackSummary {
  scenarios: number;
  accuracy: number | null;
  violations: number;
  categoryRecall: number | null;
  falsePositives: number;
  p50ms?: number | null;
  p95ms?: number | null;
  avgTokens?: number | null;
  labels?: Record<string, VisionLabelSummary>;
}
export interface VisionTrainingRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'success' | 'failed' | 'not_configured';
  stats: {
    consentedDecisions?: number;
    trainExamples?: number;
    heldOutExamples?: number;
    toFirstTraining?: number;
    workspaces?: number;
    largestWorkspaceShare?: number;
    byPack?: Record<string, number>;
    byDecision?: Record<string, number>;
    exclusions?: Record<string, number>;
  };
  error: string | null;
}
/** Where an evaluation is (eval_cursor): the fixed set, then the held-out split. */
export interface VisionEvalProgress {
  phase: 'fixed' | 'heldout' | 'done' | null;
  index: number | null;
  total: number | null;
}
export interface VisionCandidate {
  id: string;
  status: 'training' | 'trained' | 'evaluating' | 'rejected' | 'awaiting_approval' | 'released' | 'retired' | 'failed';
  base_model: string;
  tuning_job: string | null;
  train_examples: number;
  created_at: string;
  released_at: string | null;
  error: string | null;
  progress: VisionEvalProgress;
  benchmark: {
    candidate?: Record<string, VisionPackSummary>;
    production?: Record<string, VisionPackSummary>;
    productionModel?: string;
    gate?: { pass: boolean; reasons: string[] };
    notes?: string[];
  };
}
export interface VisionTrainingRequest {
  id: string;
  requested_at: string;
  min_examples: number;
  status: 'queued' | 'building' | 'done' | 'refused' | 'failed';
  run_id: string | null;
  candidate_id: string | null;
  note: string | null;
  finished_at: string | null;
}
export interface VisionBenchmarkRun {
  id: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  target_model: string | null;
  summary: { production?: Record<string, VisionPackSummary>; productionModel?: string; notes?: string[] };
  error: string | null;
  created_at: string;
  finished_at: string | null;
  progress: VisionEvalProgress;
}
export interface VisionTrainingState {
  runs: VisionTrainingRun[];
  candidates: VisionCandidate[];
  requests: VisionTrainingRequest[];
  benchmark_runs: VisionBenchmarkRun[];
  try_photo: { limit: number; used_last_hour: number };
}

export async function listVisionTraining(): Promise<VisionTrainingState> {
  const { data, error } = await supabase.rpc('admin_list_vision_training');
  if (error) throw error;
  return {
    runs: [], candidates: [], requests: [], benchmark_runs: [], try_photo: { limit: 20, used_last_hour: 0 },
    ...((data as Partial<VisionTrainingState> | null) ?? {}),
  };
}

/** "Train now": queued for the next tick (posted at once); never below the floor. */
export async function requestVisionTraining(minExamples: number): Promise<void> {
  const { error } = await supabase.rpc('admin_request_vision_training', { p_min_examples: minExamples });
  if (error) throw error;
}

/** "Benchmark the live model now": the production model alone, same scorer as a candidate. */
export async function requestVisionBenchmark(): Promise<void> {
  const { error } = await supabase.rpc('admin_request_vision_benchmark');
  if (error) throw error;
}

/** Posts the tick now (poll a tuning job, continue an evaluation) instead of waiting 15 minutes. */
export async function runVisionTrainingTick(): Promise<void> {
  const { error } = await supabase.rpc('admin_run_vision_training_tick');
  if (error) throw error;
}

export interface VisionTryObservation {
  id: string;
  kind: string;
  displayValue: string;
  confidence: number | null;
  coverage: string;
  defectCategory: string | null;
  severity: string | null;
  subject: string | null;
  packId: string | null;
  regions?: { frameId: string; box: [number, number, number, number]; label: string | null }[];
}
export interface VisionTryModelResult {
  model: string;
  ms: number;
  tokens: number | null;
  failed?: string;
  status?: number | null;
  generations?: number;
  observations?: VisionTryObservation[];
  frameQuality?: { frameId: string; quality: string }[];
  guidance?: string[];
}
export interface VisionTryResult {
  production: VisionTryModelResult;
  candidate: VisionTryModelResult | { skipped: string } | null;
  candidateStatus: string | null;
  pack: string | null;
  remaining: number;
  limit: number;
}

/**
 * "Try a photo": one photo through the live model and the latest candidate, in
 * memory only, no credits. A direct fetch (not supabase.functions.invoke) so the
 * function URL can point at a standalone Deno run locally.
 */
export async function tryVisionPhoto(input: { pack: string | null; label: string; mimeType: string; data: string }): Promise<VisionTryResult> {
  const { data: session } = await supabase.auth.getSession();
  const token = session.session?.access_token;
  if (!token) throw new Error('Sign in first.');
  const res = await fetch(`${FUNCTIONS_URL}/admin-vision-try-photo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, apikey: SUPABASE_ANON },
    body: JSON.stringify({ pack: input.pack, label: input.label, frame: { mimeType: input.mimeType, data: input.data } }),
  });
  const body = (await res.json().catch(() => null)) as (VisionTryResult & { error?: string }) | null;
  if (!res.ok) throw new Error(body?.error ?? `The function answered HTTP ${res.status}.`);
  if (!body) throw new Error('The function returned no answer.');
  return body;
}

export async function releaseVisionCandidate(id: string): Promise<void> {
  const { error } = await supabase.rpc('admin_release_vision_candidate', { p_candidate_id: id });
  if (error) throw error;
}

export async function rollbackVisionModel(): Promise<void> {
  const { error } = await supabase.rpc('admin_rollback_vision_model');
  if (error) throw error;
}

// --- AI output reports (Google Play AI-generated content policy) --------------

export interface AiOutputReport {
  id: string;
  created_at: string;
  surface: string;
  reason: 'offensive' | 'harmful' | 'wrong' | 'other';
  output: string;
  status: 'open' | 'reviewed';
  reporter_email: string | null;
  workspace_name: string | null;
}

/** Reports users sent from the app; `open` first, newest first. null = all. */
export async function listAiOutputReports(status: 'open' | null): Promise<AiOutputReport[]> {
  const { data, error } = await supabase.rpc('admin_list_ai_output_reports', { p_status: status });
  if (error) throw error;
  return (data as AiOutputReport[]) ?? [];
}

export async function reviewAiOutputReport(id: string): Promise<void> {
  const { error } = await supabase.rpc('admin_review_ai_output_report', { p_report_id: id });
  if (error) throw error;
}

// --- Plan grants and the free period ------------------------------------------
// A plan given without payment (a test workspace, a pilot, a partner) and the end
// of a workspace's free period. A live grant is full access and holds the plan
// against billing events until it ends; then a paying workspace moves to the plan
// it pays for.

export interface WorkspacePlanGrant {
  id: string;
  plan_key: string;
  starts_at: string;
  ends_at: string | null;
  reason: string;
  granted_by: string | null;
  created_at: string;
  revoked_at: string | null;
  revoked_reason: string | null;
  live: boolean;
}

export interface WorkspaceAccess {
  edition_key: string | null;
  access_state: string | null;
  billing_provider: string | null;
  paid: boolean;
  free_period_ends_at: string | null;
  free_period_fixed: boolean;
  grants: WorkspacePlanGrant[];
}

export async function getWorkspaceAccess(accountId: string): Promise<WorkspaceAccess> {
  const { data, error } = await supabase.rpc('admin_get_workspace_access', { p_account_id: accountId });
  if (error) throw new Error(error.message);
  return data as WorkspaceAccess;
}

/** endsAt null = no end. Replaces any grant that is live or scheduled. */
export async function grantWorkspacePlan(accountId: string, planKey: string, endsAt: string | null, reason: string): Promise<void> {
  const { error } = await supabase.rpc('admin_grant_workspace_plan', {
    p_account_id: accountId,
    p_plan_key: planKey,
    p_ends_at: endsAt,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

export async function revokeWorkspacePlanGrant(grantId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('admin_revoke_workspace_plan_grant', {
    p_grant_id: grantId,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

export async function setFreePeriodEnd(accountId: string, endsAt: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('admin_set_free_period_end', {
    p_account_id: accountId,
    p_ends_at: endsAt,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

// --- Feature requests and the ideas board ---------------------------------------
// What people asked for from Account > Help & support > Request a feature. Only a
// platform admin reads them; the sender hears about every status change and reply.

export type FeatureRequestStatus = 'sent' | 'under_review' | 'planned' | 'in_progress' | 'shipped' | 'not_planned' | 'merged';
export type FeatureIdeaStatus = 'considering' | 'planned' | 'in_progress' | 'shipped';

export interface AdminFeatureRequest {
  id: string;
  created_at: string;
  updated_at: string;
  title: string;
  description: string;
  category: string;
  importance: 'nice_to_have' | 'saves_time' | 'blocking';
  status: FeatureRequestStatus;
  admin_reply: string | null;
  shipped_release: string | null;
  merged_into_id: string | null;
  idea_id: string | null;
  source: 'app' | 'assistant';
  context: Record<string, string>;
  screenshot_paths: string[];
  plan_key: string | null;
  user_id: string | null;
  requester_name: string | null;
  requester_email: string | null;
  requester_blocked: boolean;
  account_id: string | null;
  workspace_name: string | null;
  vote_count: number;
  workspace_count: number;
}

export async function listFeatureRequests(): Promise<AdminFeatureRequest[]> {
  const { data, error } = await supabase.rpc('admin_list_feature_requests', { p_limit: 500 });
  if (error) throw new Error(error.message);
  return (data as AdminFeatureRequest[]) ?? [];
}

/** Status and reply in one save; the sender (and anyone merged in) is told. */
export async function updateFeatureRequest(
  requestId: string,
  status: Exclude<FeatureRequestStatus, 'merged'>,
  reply: string | null,
  shippedRelease: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('admin_update_feature_request', {
    p_request_id: requestId,
    p_status: status,
    p_reply: reply,
    p_shipped_release: shippedRelease,
  });
  if (error) throw new Error(error.message);
}

export async function mergeFeatureRequest(requestId: string, intoId: string): Promise<void> {
  const { error } = await supabase.rpc('admin_merge_feature_request', {
    p_request_id: requestId,
    p_into_id: intoId,
  });
  if (error) throw new Error(error.message);
}

export async function setFeatureRequestBlock(userId: string, blocked: boolean, reason: string | null): Promise<void> {
  const { error } = await supabase.rpc('admin_set_feature_request_block', {
    p_user_id: userId,
    p_blocked: blocked,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

/** Short-lived links to a request's screenshots (private bucket, admin read policy). */
export async function featureRequestScreenshotUrls(paths: string[]): Promise<string[]> {
  if (!paths.length) return [];
  const { data, error } = await supabase.storage.from('feature-request-screenshots').createSignedUrls(paths, 600);
  if (error) throw new Error(error.message);
  return (data ?? []).map((d) => d.signedUrl).filter((u): u is string => !!u);
}

export interface AdminFeatureIdea {
  id: string;
  title: string;
  summary: string;
  status: FeatureIdeaStatus;
  published: boolean;
  shipped_release: string | null;
  vote_count: number;
  request_count: number;
  created_at: string;
  updated_at: string;
}

export async function listFeatureIdeas(): Promise<AdminFeatureIdea[]> {
  const { data, error } = await supabase.rpc('admin_list_feature_ideas');
  if (error) throw new Error(error.message);
  return (data as AdminFeatureIdea[]) ?? [];
}

/** ideaId null creates one. fromRequestId links that request and its senders vote for the idea. */
export async function saveFeatureIdea(input: {
  ideaId: string | null;
  title: string;
  summary: string;
  status: FeatureIdeaStatus;
  published: boolean;
  shippedRelease: string | null;
  fromRequestId?: string | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc('admin_save_feature_idea', {
    p_idea_id: input.ideaId,
    p_title: input.title,
    p_summary: input.summary,
    p_status: input.status,
    p_published: input.published,
    p_shipped_release: input.shippedRelease,
    p_from_request_id: input.fromRequestId ?? null,
  });
  if (error) throw new Error(error.message);
  return data as string;
}
